import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAreas } from '../../data/geo.ts'
import { frameFor } from '../../data/lookGeometry.ts'
import type { TaskTypeRow } from '../../data/plan.ts'
import { buildHistory } from '../../data/settingsHistory.ts'
import { defaultCycleLength, proposeCycle, ruleSettings, ruleStepKeys } from '../../data/settingsRule.ts'
import { useClub } from '../../club/ClubProvider.tsx'
import { LookPicture } from '../../ui/looks/LookPicture.tsx'
import { cycleWhy, plannedUntil, readText, reasonText, type T } from './settingsText.ts'
import { useReadsWork, useSettingsData, useToday } from './useSettingsData.ts'

const MAX_STEPS = 12

/** M25-A (docs/M25.md D, "Rule form"; question 4; ADR-131): « Proposer une alternance » fills a recurrence rule's rotation steps ON THE PAGE (part S places it in
 *  the rule form: `onFill` receives one object per step, a word per choice setting). It is the written rule of `data/settingsRule.ts`, never a model: each step is
 *  chosen by the same rule as if the steps before it had been done, from the previous cut before the new version's first day (`start`), with no groomers and no
 *  typed number. The default length is the number of combinations (direction or pattern choices with a picture × the lap senses), at most 12, changeable here, and
 *  the panel prints why. Nothing is published: the form's own « Publier » sends the ordinary `recurrence.publish@1` of a new version, and the panel says that the
 *  cycle restarts at step 1 from its start date. Shown only where the viewer holds `job.plan` and `worklog.read` (rule 2). */
export function CycleProposal({ tt, areaIds, start, onFill }: { tt: TaskTypeRow | undefined; areaIds: readonly string[]; start: string; onFill(steps: Array<Record<string, string>>): void }) {
  const { t, i18n } = useTranslation()
  const reads = useReadsWork()
  const [open, setOpen] = useState(false)
  const settings = useMemo(() => ruleSettings(tt, i18n.language), [tt, i18n.language])
  if (!reads || settings.length === 0) return null
  if (!open) return <button type="button" className="btn self-start" onClick={() => setOpen(true)} data-testid="cycle-open">{t('plan.settingsCycle.open')}</button>
  return <Body tt={tt} areaIds={areaIds} start={start} onFill={onFill} onClose={() => setOpen(false)} />
}

function Body({ tt, areaIds, start, onFill, onClose }: { tt: TaskTypeRow | undefined; areaIds: readonly string[]; start: string; onFill(steps: Array<Record<string, string>>): void; onClose(): void }) {
  const { t, i18n } = useTranslation()
  const tf = t as T
  const { site } = useClub()
  const data = useSettingsData()
  const areas = useAreas()
  const today = useToday()
  const settings = useMemo(() => ruleSettings(tt, i18n.language), [tt, i18n.language])
  const cyclic = useMemo(() => settings.filter((s) => s.family !== 'tool'), [settings])
  const hasTool = settings.some((s) => s.family === 'tool')
  const [typed, setTyped] = useState<string | null>(null)
  const [filled, setFilled] = useState(false)
  const none = useMemo(() => new Set<string>(), [])
  const len = useMemo(() => defaultCycleLength(cyclic, none), [cyclic, none])
  const n = (() => { const v = typed === null ? NaN : Number(typed); return Number.isInteger(v) && v >= 1 ? Math.min(v, MAX_STEPS) : len.n })()
  const history = useMemo(() => (data ? buildHistory({
    tt, jobs: data.jobs, jobAreas: data.jobAreas, completions: data.completions, coverage: data.coverage, confirmations: data.confirmations,
    areaIds, today, until: start, tz: site.timezone, readsWork: true,
  }) : null), [data, tt, areaIds, today, start, site.timezone])
  const steps = useMemo(() => (history ? proposeCycle({ start, areaIds, settings: cyclic, lines: history.lines }, n) : []), [history, start, areaIds, cyclic, n])
  const frame = useMemo(() => { const byId = new Map((areas ?? []).map((a) => [a.id, a.area_type_code])); return frameFor(areaIds.map((id) => byId.get(id) ?? '')) }, [areas, areaIds])
  if (!data || !history) return <p className="opacity-70">{t('common.loading')}</p>

  return (
    <section className="card flex flex-col gap-3" data-testid="cycle-panel" aria-labelledby="cycle-title">
      <div className="flex items-start justify-between gap-2">
        <h3 id="cycle-title" className="text-lg font-bold">{t('plan.settingsCycle.title')}</h3>
        <button type="button" className="btn !px-4" onClick={onClose} data-testid="cycle-close">{t('plan.settingsCycle.close')}</button>
      </div>
      <p className="text-sm" data-testid="cycle-notai">{t('plan.settingsRule.notAI')}</p>
      <p className="meta" data-testid="cycle-read">{readText(tf, history, plannedUntil(start))}</p>
      {cyclic.length === 0 ? <p data-testid="cycle-nothing">{t('plan.settingsCycle.nothing')}</p> : (
        <>
          {areaIds.length === 0 && <p className="meta">{t('plan.settingsCycle.noAreas')}</p>}
          <div className="flex flex-col gap-1">
            <label htmlFor="cycle-steps" className="text-sm font-semibold">{t('plan.settingsCycle.stepsLabel')}</label>
            <input id="cycle-steps" className="input sm:max-w-xs" inputMode="numeric" value={typed ?? String(len.n)} onChange={(e) => { setTyped(e.target.value); setFilled(false) }} data-testid="cycle-steps" />
            <p className="meta">{t('plan.settingsCycle.stepsHelp')}</p>
            <p className="text-sm font-semibold" data-testid="cycle-why">{cycleWhy(tf, cyclic, none)}</p>
          </div>
          <ol className="flex flex-col gap-1.5" data-testid="cycle-steps-list">
            {steps.map((s) => (
              <li key={s.index} className="rounded-xl border border-slate-200 p-2 dark:border-night-line" data-testid="cycle-step" data-step={s.index + 1}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-bold">{t('plan.settingsCycle.stepN', { n: s.index + 1 })}</span>
                  {cyclic.map((st) => { const p = s.picks[st.key]; return p ? (
                    <span key={st.key} className="inline-flex items-center gap-1.5">
                      <LookPicture code={p.code} frame={frame} className="size-8 shrink-0 text-brand dark:text-fairway" />
                      <span data-testid={`cycle-step-${st.family}`}>{p.word}</span>
                    </span>
                  ) : null })}
                </div>
                <details>
                  <summary className="min-h-14 cursor-pointer content-center text-sm font-semibold">{t('plan.settingsTrack.reasons')}</summary>
                  <ul className="meta flex flex-col gap-0.5">
                    {cyclic.map((st) => { const p = s.picks[st.key]; return p ? <li key={st.key}><span className="font-semibold">{st.label}</span> : {reasonText(tf, p.reason)}</li> : null })}
                  </ul>
                </details>
              </li>
            ))}
          </ol>
          {hasTool && <p className="meta">{t('plan.settingsCycle.groomersKept')}</p>}
          <p className="text-sm" data-testid="cycle-restart">{t('plan.settingsCycle.restart')}</p>
          <p className="meta">{t('plan.settingsCycle.fillHelp')}</p>
          <button type="button" className="btn btn-primary" onClick={() => { onFill(steps.map((s) => s.values)); setFilled(true) }} disabled={steps.length === 0} data-testid="cycle-fill">{t('plan.settingsCycle.fill')}</button>
          {filled && <p className="text-sm font-semibold" role="status">{t('plan.settingsCycle.filled')}</p>}
          <details className="rounded-xl bg-fairway-light p-3 text-sm dark:bg-night">
            <summary className="min-h-14 cursor-pointer content-center font-semibold">{t('plan.settingsRule.title')}</summary>
            <ol className="mt-1 list-decimal pl-5" data-testid="cycle-rule">{ruleStepKeys(cyclic).map((k) => <li key={k} className="py-0.5">{t(k)}</li>)}</ol>
          </details>
        </>
      )}
    </section>
  )
}
